import * as z from "zod";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TriangleAlert, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { catchError } from "@/lib/errors";
import { handleApiError } from "@/utils/errors";
import { settingsRepo } from "@/lib/repositories";
import useAuth from "@/features/auth/hooks/useAuth";

const DeleteAccountSetting = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const deleteSchema = z.object({
    password: z.string().min(1),
  });

  const form = useForm<z.infer<typeof deleteSchema>>({
    resolver: zodResolver(deleteSchema),
    defaultValues: { password: "" },
  });

  const onSubmit = async (data: z.infer<typeof deleteSchema>) => {
    if (!user?.id) return;
    setIsLoading(true);
    const [error] = await catchError(settingsRepo.deleteAccount(user.id, data));
    if (error) {
      toast.error(handleApiError(error).message);
      setIsLoading(false);
    } else {
      toast.success(t("settings.delete_account.success"));
      setOpen(false);
      await logout();
    }
  };

  const handleOpenChange = (value: boolean) => {
    if (isLoading) return;
    setOpen(value);
    if (!value) form.reset();
  };

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2 text-sm font-medium text-destructive">
        <TriangleAlert className="size-4" />
        {t("settings.delete_account.title")}
      </Label>
      <p className="text-muted-foreground text-sm">
        {t("settings.delete_account.description")}
      </p>
      <Button variant="destructive" size="sm" onClick={() => setOpen(true)}>
        {t("settings.delete_account.button")}
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>
              {t("settings.delete_account.dialog_title")}
            </DialogTitle>
            <DialogDescription>
              {t("settings.delete_account.dialog_description")}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={form.handleSubmit(onSubmit)} id="delete-account-form">
            <FieldGroup>
              <Controller
                name="password"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor="delete-account-password">
                      {t("settings.delete_account.password_label")}
                    </FieldLabel>
                    <Input
                      {...field}
                      id="delete-account-password"
                      type="password"
                      aria-invalid={fieldState.invalid}
                      disabled={isLoading}
                    />
                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                )}
              />
            </FieldGroup>
          </form>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={isLoading}>
                {t("keys.cancel")}
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              type="submit"
              form="delete-account-form"
              disabled={isLoading}
            >
              {isLoading && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t("settings.delete_account.confirm_button")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeleteAccountSetting;
